import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import { api, getApiErrorMessage } from "@/lib/api"
import type { Board } from "@/lib/task-boards"

/**
 * Archive or unarchive one board (FOR-349). Every board list, the board itself
 * and the task lists (whose rows carry `board.archived_at`) refetch afterwards,
 * so the sidebar and pickers follow at once.
 */
export function useSetBoardArchived() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ board, archived }: { board: Pick<Board, "key" | "name">; archived: boolean }) =>
      api.boards.update(board.key, { archived }),
    onSuccess: (_data, { board, archived }) => {
      queryClient.invalidateQueries({ queryKey: ["boards"] })
      queryClient.invalidateQueries({ queryKey: ["board", board.key] })
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
      toast({ title: archived ? `${board.name} archived` : `${board.name} unarchived` })
    },
    onError: (error, { archived }) => {
      toast({
        title: archived ? "Not archived" : "Not unarchived",
        description: getApiErrorMessage(error),
        variant: "destructive",
      })
    },
  })
}
